import { useFormContext } from 'app/hooks/form';
import { useUser } from 'app/network/user';
import { Row } from 'app/shopify';
import type { CreateListingDTO } from 'app/types/listing';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionModeToggle } from './transaction-mode-toggle';

export function TransactionModeSection() {
  const { t } = useTranslation('new');
  const { data: user } = useUser();
  const transactionModeEnabled = isFeatureEnabled(FeatureFlag.transactionMode, user?.featureFlags);

  const { setValue, watch } = useFormContext<CreateListingDTO>();

  const watchSubtype = watch('subtype');
  const watchType = watch('type');
  const watchTransactionMode = watch('transactionMode');
  const isExpirable = watchSubtype === ListingSubtype.expirable;
  const isObject = watchSubtype === ListingSubtype.object;

  const transactionModeOptions = (() => {
    const translationPrefix =
      watchType === ListingType.request
        ? 'itemInformationSection.requestTransactionMode'
        : 'itemInformationSection.transactionMode';
    const options = [
      { label: t(`${translationPrefix}.sell`), value: TransactionMode.sell },
      { label: t(`${translationPrefix}.lend`), value: TransactionMode.lend, disabled: isExpirable },
      { label: t(`${translationPrefix}.give`), value: TransactionMode.give },
    ];

    return options.filter((option) => !option.disabled);
  })();

  const shouldShowTransactionMode =
    isWhiteLabel(WhiteLabel.HauteGaspesie) && transactionModeEnabled && (isObject || isExpirable);

  useEffect(() => {
    if (!shouldShowTransactionMode) {
      if (watchTransactionMode !== null) {
        setValue('transactionMode', null, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    const validOptions = transactionModeOptions.map((option) => option.value);
    if (!watchTransactionMode || !validOptions.includes(watchTransactionMode)) {
      setValue('transactionMode', TransactionMode.sell, { shouldDirty: true, shouldValidate: true });
    }
  }, [setValue, shouldShowTransactionMode, transactionModeOptions, watchTransactionMode]);

  const handleTransactionModeChange = (mode: TransactionMode) => {
    setValue('transactionMode', mode, { shouldValidate: true });
    if (watchType === ListingType.request) {
      setValue('price', null, { shouldValidate: true });
    } else if (mode === TransactionMode.sell) {
      setValue('estimatedPrice', null, { shouldValidate: true });
    }
  };

  if (!shouldShowTransactionMode) return null;

  return (
    <Row
      backgroundColor="surface.primary-neutral"
      borderRadius="radius.md"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
    >
      <TransactionModeToggle
        onChange={handleTransactionModeChange}
        options={transactionModeOptions}
        value={watchTransactionMode}
        wrapOnSmallUI={watchType !== ListingType.request}
      />
    </Row>
  );
}
